import React, { useRef } from 'react';
import { CargoTankList } from './CargoTempLogReporting.style';
import { TankDetails } from '../CargoTemperatureLogUtils';
import { CargoTemperatureLogEntry } from '../../../types/ViewModel/CargoTempLog/CargoTemperatureLog';
import { InputChangeData } from '../../common/odfjell-input-component/OdfjellInputComponent';
import CargoLogTableHeader from './CargoLogTableHeader';
import CargoLogRow from './CargoLogRow';

type CargoTempLogTableProps = {
    cargoTanks: TankDetails[];
    activeLogSchedules: string[];
    sortColumn: number;
    sortDesc: boolean;
    isCommentsVisible: boolean;
    isReadOnly: boolean;
    oxygenUnitType: string;
    editComment: boolean;
    editCommentCargoLog: CargoTemperatureLogEntry;
    commentInput: string;
    cargoTemperatureInputFields: any[];
    cargoTemperatureConfigs: any[];
    changeSort: (column: number) => void;
    onCargoLogValueChange: (changeObject: InputChangeData) => void;
    onCargoLogIsOnBoardChange: (event: React.ChangeEvent<HTMLInputElement>, cargoLog: CargoTemperatureLogEntry) => void;
    onCargoLogCommentChange: (comment: string, cargoLog: CargoTemperatureLogEntry) => void;
    openComment: (cargoLog: CargoTemperatureLogEntry) => void;
    getNumberOfInputFields: (cargoNumber: string) => number;
    date: Date;
};

const CargoTempLogTable: React.FC<CargoTempLogTableProps> = ({
    cargoTanks,
    activeLogSchedules,
    sortColumn,
    sortDesc,
    isCommentsVisible,
    isReadOnly,
    oxygenUnitType,
    editComment,
    editCommentCargoLog,
    commentInput,
    cargoTemperatureInputFields,
    cargoTemperatureConfigs,
    changeSort,
    onCargoLogValueChange,
    onCargoLogIsOnBoardChange,
    onCargoLogCommentChange,
    openComment,
    getNumberOfInputFields,
    date,
}) => {
    const tableRef = useRef(null);

    /**
     * Navigate input fields using arrow keys, based on tabindex
     * @param e keyevent
     */
    const onKeyDown = (e) => {
        if (e?.code === 'ArrowDown' || e?.code === 'ArrowUp') {
            // get all input fields in the cargo temp log table
            const inputFields: HTMLInputElement[] = Array.from(tableRef.current.querySelectorAll('input')) || [];
            if (!inputFields || inputFields.length === 0) return;
            // remove elements with tabindex 0
            const fields = inputFields.filter((f) => f.tabIndex !== 0);
            if (!fields || fields.length === 0) return;
            const position = fields.indexOf(e.target as HTMLInputElement);
            const tabI = fields[position]?.tabIndex;
            if (!tabI) return;

            if (e.code === 'ArrowDown') {
                const fieldsAfter = fields.filter((f) => f.tabIndex > tabI);
                let nextField: HTMLInputElement = null;
                if (fieldsAfter && fieldsAfter.length > 0) {
                    // next input field in tabindex order
                    nextField = fieldsAfter.reduce((prev, curr) => (curr.tabIndex < prev.tabIndex ? curr : prev));
                } else {
                    // return to first input field in tabindex order when pressing down arrow at last field
                    nextField = fields.reduce((prev, curr) => (curr.tabIndex < prev.tabIndex ? curr : prev));
                }
                nextField && nextField.focus();
            } else if (e.code === 'ArrowUp') {
                const fieldsBefore = fields.filter((f) => f.tabIndex < tabI);
                let prevField: HTMLInputElement = null;
                if (fieldsBefore && fieldsBefore.length > 0) {
                    // previous input field in tabindex order
                    prevField = fieldsBefore.reduce((prev, curr) => (curr.tabIndex > prev.tabIndex ? curr : prev));
                } else {
                    // last input field in tabindex order
                    prevField = fields.reduce((prev, curr) => (curr.tabIndex > prev.tabIndex ? curr : prev));
                }
                prevField && prevField.focus();
            }
        }
    };

    const getOxygenUnit = (cargoNumber: string): string => {
        const config = cargoTemperatureConfigs.find((c) => c.cargoNumber === cargoNumber);
        return config?.oxygenUnitType || oxygenUnitType;
    };

    return (
        <>
            <CargoLogTableHeader
                sortColumn={sortColumn}
                sortDesc={sortDesc}
                changeSort={changeSort}
                activeLogSchedules={activeLogSchedules}
                isCommentsVisible={isCommentsVisible}
                cargoTemperatureInputFields={cargoTemperatureInputFields}
            />
            <CargoTankList onKeyDown={onKeyDown} ref={tableRef}>
                {cargoTanks &&
                    cargoTanks
                        .filter(
                            (c) =>
                                cargoTemperatureConfigs.findIndex((conf) => conf.cargoNumber === c.cargoNumber && conf.inputFields.length > 0) !== -1
                        )
                        .map((tankDetails, i) => (
                            <CargoLogRow
                                key={`cargo_tank_row_${tankDetails.cargoNumber}_${i}`}
                                tankDetails={tankDetails}
                                index={i}
                                activeLogSchedules={activeLogSchedules}
                                isCommentsVisible={isCommentsVisible}
                                isReadOnly={isReadOnly}
                                oxygenUnitType={getOxygenUnit(tankDetails.cargoNumber)}
                                editComment={editComment}
                                editCommentCargoLog={editCommentCargoLog}
                                commentInput={commentInput}
                                onCargoLogValueChange={onCargoLogValueChange}
                                onCargoLogIsOnBoardChange={onCargoLogIsOnBoardChange}
                                onCargoLogCommentChange={onCargoLogCommentChange}
                                openComment={openComment}
                                cargoTanks={cargoTanks}
                                getNumberOfInputFields={getNumberOfInputFields}
                                config={cargoTemperatureConfigs.find((c) => c.cargoNumber === tankDetails.cargoNumber) || {}}
                                date={date}
                            />
                        ))}
            </CargoTankList>
        </>
    );
};

export default CargoTempLogTable;
